import { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

// Components and Molecules
import HomeHero from "./HomeHero";
import HomePartners from "./HomePartners";
import HomeAbout from "./HomeAbout";
import HomeTestimonials from "./HomeTestimonials";
import HomeLocations from "./HomeLocations";
import HomeFaq from "./HomeFaq";
import HomeBeginJourney from "./HomeBeginJourney";
import Button from "../../../components/Desktop/Button";

import config from "../../../config";

// Images and Icons
import sect1A from "../../../assets/img/HomePage/Desktop/sect1_a.png";
import sect1B from "../../../assets/img/HomePage/Desktop/sect1_b.png";
import sect2A from "../../../assets/img/HomePage/Desktop/sect2_a.png";
import sect2B from "../../../assets/img/HomePage/Desktop/sect2_b.png";

import { ReactComponent as CheckIconFilled } from "../../../icons/CheckIconFilled.svg";

var webUrl = config?.webUrl;

const HomeSections = () => {
  return (
    <>
      {/* Hero Section */}
      <HomeHero />

      {/* Partners Section */}
      {/* <HomePartners /> */}

      {/* Section One */}
      <HomeSection homeSectionVariant="image-first" />

      {/* Section Two */}
      <HomeSection homeSectionVariant="text-first" />

      {/* Partners Section */}
      <HomePartners />

      {/* About Us Section */}
      <HomeAbout />

      {/* Testimonials Section */}
      <HomeTestimonials />

      {/* Locations Section */}
      <HomeLocations />

      {/* FAQ Section */}
      <HomeFaq />

      {/* Begin Journey Section */}
      <HomeBeginJourney />
    </>
  );
};

type HomeSectionProps = {
  homeSectionVariant: "image-first" | "text-first";
};

const HomeSection: FC<HomeSectionProps> = ({ homeSectionVariant }) => {
  if (homeSectionVariant === "image-first") {
    return (
      <section className="px-[120px] py-[100px] pt-[140px] bg-[#F8F8F8] ">
        <div className="flex items-center justify-between gap-[64px] max-w-[1550px] mx-auto">
          {/* Image Box */}
          <HomeSectionImage imageA={sect1A} imageB={sect1B} />

          {/* Text Box */}
          <TextBox
            pointVariant="food-entrepreneurs"
            heading={{
              subHead: "Food Entrepreneurs",
              mainHead: (
                <>
                  <span className="text-primaryDark-400">Expand Beyond</span>{" "}
                  Your Home Kitchen.
                </>
              ),
              description: (
                <>
                  Focus on what your culinary dream looks like and let HomeCook
                  take care of the how.
                </>
              ),
            }}
            button={{
              label: "View Resource Pantry",
              onClick: () => {
                window.open(webUrl, "_blank");
              },
            }}
          />
        </div>
      </section>
    );
  }

  const bookDemoUrl =
    "https://calendly.com/homecook/homecook-resource-provider-demo";

  return (
    <section className="px-[120px] py-[100px] pt-[140px]">
      <div className="flex items-center justify-between gap-[64px] max-w-[1550px] mx-auto">
        {/* Text Box */}
        <TextBox
          pointVariant="resource-providers"
          heading={{
            subHead: "Resource Providers",
            mainHead: (
              <>
                <span className="text-primaryDark-400"> Monetize Your </span>{" "}
                Additional Resources
              </>
            ),
            description: (
              <>
                Monetize your additional resources and services to support food
                entrepreneurship in your community.
              </>
            ),
          }}
          button={{
            label: "Book Demo",
            onClick: () => {
              window.open(bookDemoUrl, "_blank");
            },
          }}
        />

        {/* Image Box */}
        <HomeSectionImage imageGreyBg imageA={sect2A} imageB={sect2B} />
      </div>
    </section>
  );
};

type TextBoxProps = {
  pointVariant: "food-entrepreneurs" | "resource-providers";
  heading: {
    subHead: string;
    mainHead: ReactNode;
    description: ReactNode;
  };
  button: {
    label: string;
    onClick: () => void;
  };
};

const TextBox: FC<TextBoxProps> = ({ heading, pointVariant, button }) => {
  const navigate = useNavigate();

  if (pointVariant === "food-entrepreneurs") {
    return (
      <div className="max-w-[525px]">
        {/* Heading */}
        <div className="mb-[24px]">
          <p className="text-[16px] font-open-sans font-semibold text-primaryDark-0 mb-[8px]">
            {heading.subHead}
          </p>

          <h3 className="text-[36px] font-raleway font-bold leading-tight text-primaryDark-0 mb-[8px]">
            {heading.mainHead}
          </h3>

          <p className="text-[16px] text-text-300 font-open-sans leading-loose">
            {heading.description}
          </p>
        </div>

        {/* Divider */}
        <div className="w-full h-[3px] bg-primaryDark-0 mb-[24px]"></div>

        {/* Points */}
        <div className="w-full mb-[24px] flex flex-col gap-[16px] flex-shrink-0">
          <TextBoxPoint point="Gain access to a network of approved and well-equipped commercial kitchens." />

          <TextBoxPoint point="Easily find and book kitchen spaces that match your specific needs." />

          <TextBoxPoint point="Receive guidance on regulatory requirements such as licensing and permits." />

          <TextBoxPoint point="Stay informed about crucial food safety regulations." />

          <TextBoxPoint point="Leverage HomeCook’s community features to build valuable connections." />
        </div>

        {/* Button */}
        <div className="flex items-center gap-[16px]">
          <Button
            label={button.label}
            variant="secondary"
            onClick={button.onClick}
          />
          <Button
            label="Learn More"
            variant="primary"
            onClick={() => navigate("/food-entrepreneurs")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[525px]">
      {/* Heading */}
      <div className="mb-[24px]">
        <p className="text-[16px] font-open-sans font-semibold text-primaryDark-0 mb-[8px]">
          {heading.subHead}
        </p>

        <h3 className="text-[36px] font-raleway font-bold leading-tight text-primaryDark-0 mb-[8px]">
          {heading.mainHead}
        </h3>

        <p className="text-[16px] text-text-300 font-open-sans leading-loose">
          {heading.description}
        </p>
      </div>

      {/* Divider */}
      <div className="w-full h-[3px] bg-primaryDark-0 mb-[24px]"></div>

      {/* Points */}
      <div className="w-full mb-[24px] flex flex-col gap-[16px] flex-shrink-0">
        <TextBoxPoint point="Unlock new income channels by effortlessly connecting with food entrepreneurs seeking commercial kitchen spaces." />

        <TextBoxPoint point="Support & Access to a platform that helps streamline your kitchen management and rental processes." />

        <TextBoxPoint point="Leverage a user friendly platform to manage booking acceptances based on preferences." />

        <TextBoxPoint point="Empower control over community access, including membership approval and status management." />

        <TextBoxPoint point="Access to HomeCook’s vibrant culinary community." />
      </div>

      {/* Button */}
      <div className="flex items-center gap-[16px]">
        <Button
          label={button.label}
          variant="secondary"
          onClick={button.onClick}
        />
        <Button
          label="Learn More"
          variant="primary"
          onClick={() => navigate("/resource-providers")}
        />
      </div>
    </div>
  );
};

type TextBoxPointProps = {
  point: string;
};

const TextBoxPoint: FC<TextBoxPointProps> = ({ point }) => {
  return (
    <div className="flex items-center gap-[8px]">
      <CheckIconFilled className="text-primaryDark-400 w-[20px] h-[20px]" />
      <p className="w-[calc(100%-20px)] text-[16px] font-open-sans font-medium text-text-0 leading-[1.5]">
        {point}
      </p>
    </div>
  );
};

type HomeSectionImageProps = {
  imageGreyBg?: boolean;
  imageA: string;
  imageB: string;
};

const HomeSectionImage: FC<HomeSectionImageProps> = ({
  imageGreyBg,
  imageA,
  imageB,
}) => {
  return (
    <div className="w-[730px] relative z-0">
      {/* Image Background */}
      <div
        className={`w-full h-[687px] ${imageGreyBg ? "bg-[#F8F8F8]" : "bg-[#FFFFFF]"} rounded-[16px] absolute bottom-0`}
      ></div>

      {/* Images */}
      <div className="relative z-2 bottom-[40px] w-[670px] h-[940px] bg-text-1000 rounded-[8px] shadow-xl shadow-text-0/24 mx-auto">
        <div>
          <img
            src={imageA}
            alt="Sect1_imga"
            className="w-[640px] object-cover absolute z-10 top-[20px] left-[50%] translate-x-[-50%] bg-[text-1000]"
          />
          <img
            src={imageB}
            alt="Sect1_imgb"
            className="w-[640px] object-cover absolute z-3 bottom-[20px] left-[50%] translate-x-[-50%] bg-text-1000"
          />
        </div>
      </div>
    </div>
  );
};

export default HomeSections;
